// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/tapLog.ts
// 🔧 PURPOSE: IL REGISTRO DEI TOCCHI, per capire un difetto segnalato dal
//             proprietario (set 2026): a volte i pulsanti e la barra in basso
//             "non rispondono" (si scorre ma non si tocca) o si apre la pagina
//             sbagliata. Si legge dal pannello nascosto (5 tocchi sullo stemma
//             della Home, StatusBarProbe) e si copia per mandarlo in chat.
//
//             Per ogni tocco: dove (x, y), cosa c'era sotto il dito, il
//             pulsante/link che lo contiene, la pagina, lo stato di <body>
//             (pointer-events, blocco dello scorrimento) e quante finestre
//             (dialog) erano aperte. Un tocco a cui non segue il "click"
//             entro 700 ms è segnato PERSO. In più: il cambio di pagina, il
//             ritardo del thread principale (> 300 ms: l'app era occupata e i
//             tocchi aspettavano), l'app in primo piano/sfondo e il cambio
//             del service worker (versione nuova).
//
//             Leggero: solo in memoria (ultimi 200) e copiato nel telefono
//             (localStorage, con try/catch) al massimo ogni 2 secondi, così
//             sopravvive anche a un ricaricamento dell'app. Niente rete.
// ============================================

export type TapLogKind = 'start' | 'down' | 'click' | 'lost' | 'nav' | 'lag' | 'visible' | 'hidden' | 'sw' | 'error';

export interface TapLogEntry {
  /** Ora (ms dall'epoca). */
  t: number;
  k: TapLogKind;
  /** Pagina (path + query). */
  p: string;
  /** Testo libero: cosa, dove, stato. */
  m: string;
}

const STORAGE_KEY = 'nva-tap-log';
const MAX_ENTRIES = 200;
const SAVE_EVERY_MS = 2000;
const LOST_AFTER_MS = 700;
const LAG_TICK_MS = 500;
const LAG_MIN_MS = 300;

let entries: TapLogEntry[] = [];
let started = false;
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let pendingDown: { at: number; timer: ReturnType<typeof setTimeout>; desc: string; x: number; y: number } | null = null;
let lastPath = '';
let expectedTick = 0;
const listeners = new Set<() => void>();

const here = (): string => {
  try {
    return window.location.pathname + window.location.search;
  } catch {
    return '';
  }
};

function save(): void {
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
      /* spazio pieno o storage bloccato: resta in memoria */
    }
  }, SAVE_EVERY_MS);
}

export function logTap(k: TapLogKind, m: string): void {
  entries.push({ t: Date.now(), k, p: here(), m });
  if (entries.length > MAX_ENTRIES) entries = entries.slice(entries.length - MAX_ENTRIES);
  save();
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* il pannello non deve rompere il registro */
    }
  });
}

/** Un elemento in poche parole: tag#id.classe[aria-label]"testo". */
export function describeElement(el: Element | null): string {
  if (!el) return '(niente)';
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  const cls = typeof el.className === 'string' && el.className.trim()
    ? `.${el.className.trim().split(/\s+/).slice(0, 3).join('.')}`
    : '';
  const aria = el.getAttribute('aria-label');
  const role = el.getAttribute('role');
  const text = (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 40);
  return `${tag}${id}${cls}${role ? `[role=${role}]` : ''}${aria ? `[aria=${aria.slice(0, 40)}]` : ''}${text ? ` "${text}"` : ''}`;
}

/** Lo stato che può "bloccare" i tocchi: body senza tocchi, scorrimento bloccato, finestre aperte. */
function pageState(): string {
  try {
    const body = document.body;
    const pe = window.getComputedStyle(body).pointerEvents;
    const locked = body.hasAttribute('data-scroll-locked') || body.style.overflow === 'hidden';
    const dialogs = document.querySelectorAll('[role="dialog"], [aria-modal="true"]').length;
    return `body:${pe}${locked ? ' scroll-bloccato' : ''} finestre:${dialogs}`;
  } catch {
    return '';
  }
}

function onPointerDown(e: PointerEvent): void {
  const target = e.target instanceof Element ? e.target : null;
  const top = typeof document.elementFromPoint === 'function' ? document.elementFromPoint(e.clientX, e.clientY) : null;
  const actionable = target?.closest('a,button,[role="button"],[role="tab"],input,label,select,textarea') || null;
  const desc = `(${Math.round(e.clientX)},${Math.round(e.clientY)}) sotto:${describeElement(top)}`
    + `${actionable && actionable !== top ? ` pulsante:${describeElement(actionable)}` : ''} ${pageState()}`;
  if (pendingDown) {
    clearTimeout(pendingDown.timer);
    pendingDown = null;
  }
  const at = Date.now();
  pendingDown = {
    at,
    desc,
    x: e.clientX,
    y: e.clientY,
    timer: setTimeout(() => {
      if (pendingDown && pendingDown.at === at) {
        // Nessun click: un trascinamento (scorrimento) o un tocco perso.
        logTap('lost', `TOCCO PERSO: nessun click dopo ${LOST_AFTER_MS}ms ${desc}`);
        pendingDown = null;
      }
    }, LOST_AFTER_MS),
  };
  logTap('down', desc);
}

/** Il dito si è mosso: era uno scorrimento, non un tocco (niente "PERSO"). */
function onPointerUp(e: PointerEvent): void {
  if (!pendingDown) return;
  if (Math.abs(e.clientX - pendingDown.x) > 10 || Math.abs(e.clientY - pendingDown.y) > 10) {
    clearTimeout(pendingDown.timer);
    pendingDown = null;
  }
}

function onClick(e: MouseEvent): void {
  if (pendingDown) {
    clearTimeout(pendingDown.timer);
    const delay = Date.now() - pendingDown.at;
    pendingDown = null;
    logTap('click', `dopo ${delay}ms su:${describeElement(e.target instanceof Element ? e.target : null)}${e.defaultPrevented ? ' (annullato)' : ''}`);
    return;
  }
  logTap('click', `senza tocco prima su:${describeElement(e.target instanceof Element ? e.target : null)}`);
}

/** La pagina è cambiata (la chiama il Layout a ogni cambio di indirizzo). */
export function logNavigation(path: string): void {
  if (path === lastPath) return;
  lastPath = path;
  logTap('nav', `→ ${path} ${pageState()}`);
}

/** Avvia il registro una volta sola (index.tsx). */
export function startTapLog(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    if (Array.isArray(saved)) entries = saved.slice(-MAX_ENTRIES);
  } catch {
    entries = [];
  }
  let sw = 'no';
  try {
    sw = navigator.serviceWorker?.controller ? 'attivo' : 'nessuno';
  } catch {
    /* niente */
  }
  logTap('start', `app aperta · service worker ${sw} · ${navigator.userAgent.slice(0, 90)}`);
  lastPath = here();

  document.addEventListener('pointerdown', onPointerDown, { capture: true, passive: true });
  document.addEventListener('pointerup', onPointerUp, { capture: true, passive: true });
  // Il browser si è preso il gesto (scorrimento): non è un tocco perso.
  document.addEventListener('pointercancel', () => {
    if (pendingDown) {
      clearTimeout(pendingDown.timer);
      pendingDown = null;
    }
  }, { capture: true, passive: true });
  document.addEventListener('click', onClick, { capture: true });
  document.addEventListener('visibilitychange', () => {
    // Tornando in primo piano il timer era fermo: non è un ritardo dell'app.
    expectedTick = Date.now() + LAG_TICK_MS;
    logTap(document.visibilityState === 'hidden' ? 'hidden' : 'visible', document.visibilityState === 'hidden' ? 'app in sfondo' : 'app in primo piano');
  });
  try {
    navigator.serviceWorker?.addEventListener('controllerchange', () => logTap('sw', 'versione nuova dell\'app: si ricarica'));
  } catch {
    /* niente */
  }
  window.addEventListener('error', (e) => logTap('error', String((e as ErrorEvent).message || 'errore').slice(0, 160)));

  // Il thread principale occupato: il timer arriva in ritardo, e i tocchi
  // aspettano (si scorre, ma non si "tocca").
  expectedTick = Date.now() + LAG_TICK_MS;
  setInterval(() => {
    const now = Date.now();
    const lag = now - expectedTick;
    expectedTick = now + LAG_TICK_MS;
    if (lag > LAG_MIN_MS && document.visibilityState === 'visible') logTap('lag', `app occupata per ${lag}ms ${pageState()}`);
  }, LAG_TICK_MS);
}

export function getTapLog(): TapLogEntry[] {
  return entries.slice();
}

export function clearTapLog(): void {
  entries = [];
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* niente */
  }
  listeners.forEach((fn) => fn());
}

export function subscribeTapLog(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Il registro come testo, una riga per voce (da copiare in chat). */
export function formatTapLog(list: TapLogEntry[] = entries): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return list.map((e) => {
    const d = new Date(e.t);
    const time = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, '0')}`;
    return `${time} ${e.k.toUpperCase()} ${e.p} ${e.m}`;
  }).join('\n');
}
